import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { CircleCheckIcon } from "../../icons/CircleCheckIcon";
import { CloseIcon } from "../../icons/CloseIcon";
import { ErrorIcon } from "../../icons/ErrorIcon";
import { GiftIcon } from "../../icons/GiftIcon";
import { InfoIcon } from "../../icons/InfoIcon";
import { WarningIcon } from "../../icons/WarningIcon";

const alertIcons = {
	success: <CircleCheckIcon className="h-5 w-5 text-success" aria-hidden="true" />,
	error: <ErrorIcon className="h-5 w-5 text-critical" aria-hidden="true" />,
	warning: <WarningIcon className="h-5 w-5 text-warning" aria-hidden="true" />,
	info: <InfoIcon className="h-5 w-5 text-info" />,
	newFeature: <GiftIcon className="h-5 w-5 text-neutral" />,
	neutral: "",
	basic: "",
};

export const alertStyles = cva(["rounded-md"], {
	variants: {
		intent: {
			success: "border border-success bg-success-faded text-success",
			error: "border border-critical bg-critical-faded text-critical",
			warning: "border border-warning bg-warning-faded text-warning",
			info: "border border-info bg-info-faded text-info",
			newFeature: "bg-primary-faded text-neutral",
			neutral: "bg-neutral-faded",
			basic: "",
		},
		size: {
			sm: ["p-2"],
			md: ["p-4"],
		},
		defaultVariants: {
			intent: "info",
			size: "md",
		},
	},
});

type AlertContentProps =
	| {
			title: string | React.ReactNode;
			message?: string | React.ReactNode;
	  }
	| {
			title?: string | React.ReactNode;
			message: string | React.ReactNode;
	  };

type AlertProps = AlertContentProps & {
	type: VariantProps<typeof alertStyles>["intent"];
	referenceId?: string;
	size?: VariantProps<typeof alertStyles>["size"];
	dismissProps?: {
		i18n: { closeButton: string };
		dismiss: () => void;
	};
	dismissButton?: React.ReactNode;
};

export const Alert = ({ type, title, referenceId, message, size, dismissProps, dismissButton }: AlertProps) => {
	const dismissAction = dismissButton
		? dismissButton
		: dismissProps && (
				<button
					type="button"
					className="my-auto rounded-md text-icon-neutral hover:cursor-pointer hover:text-icon-neutral-hover"
					onClick={dismissProps.dismiss}
				>
					<span className="sr-only">{dismissProps.i18n.closeButton}</span>
					<CloseIcon className="h-6 w-6" aria-hidden="true" />
				</button>
			);

	return (
		<div
			className={alertStyles({ intent: type, size: size ?? "md" })}
			aria-live={type === "error" ? "assertive" : undefined}
			aria-atomic={type === "error" ? "true" : undefined}
		>
			<div className="flex w-full">
				{type && <div className="flex">{alertIcons[type]}</div>}
				<div className="ml-3 grow space-y-2 text-sm">
					{title ? (
						<div className="flex items-start justify-between gap-2">
							<h3 className="font-semibold">{title}</h3>
							{dismissAction}
						</div>
					) : (
						(message || referenceId || dismissAction) && (
							<div className="flex items-start justify-between gap-2">
								<div className="grow space-y-2">
									{referenceId && <p className="text-critical underline">REF#: {referenceId}</p>}
									{message && <div>{message}</div>}
								</div>
								{dismissAction}
							</div>
						)
					)}
					{title && referenceId && <p className="text-critical underline">REF#: {referenceId}</p>}
					{title && message && <div>{message}</div>}
				</div>
			</div>
		</div>
	);
};
